import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO, { pageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ChevronLeft, Shield } from 'lucide-react';

const PrivacyPage = () => {
  const { language, getRoute } = useLanguage();

  const content = {
    fr: {
      title: 'Politique de confidentialité',
      lastUpdated: 'Dernière mise à jour: Janvier 2026',
      intro: `${siteConfig.businessName} s'engage à protéger la confidentialité de vos informations personnelles. Cette politique explique comment nous collectons, utilisons et protégeons vos données.`,
      sections: [
        {
          title: 'Informations collectées',
          content: `Nous collectons uniquement les informations nécessaires pour fournir nos services de taxi:
          • Nom et coordonnées (téléphone, email)
          • Adresses de prise en charge et de destination
          • Informations de réservation (date, heure, nombre de passagers)
          • Préférences de service (minivan, accessible, etc.)`
        },
        {
          title: 'Utilisation des informations',
          content: `Vos informations sont utilisées pour:
          • Traiter vos réservations de taxi
          • Vous contacter concernant votre course
          • Améliorer nos services
          • Respecter les obligations légales`
        },
        {
          title: 'Partage des informations',
          content: `Nous ne vendons ni ne partageons vos informations personnelles avec des tiers, sauf:
          • Avec nos chauffeurs pour effectuer votre course
          • Si requis par la loi
          • Pour protéger nos droits légaux`
        },
        {
          title: 'Sécurité',
          content: `Nous prenons des mesures raisonnables pour protéger vos informations contre tout accès non autorisé, perte ou destruction.`
        },
        {
          title: 'Vos droits',
          content: `Vous avez le droit de:
          • Accéder à vos informations personnelles
          • Demander la correction d'informations inexactes
          • Demander la suppression de vos données
          
          Pour exercer ces droits, contactez-nous au ${siteConfig.phone}.`
        },
      ],
    },
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: January 2026',
      intro: `${siteConfig.businessName} is committed to protecting the privacy of your personal information. This policy explains how we collect, use, and protect your data.`,
      sections: [
        {
          title: 'Information Collected',
          content: `We only collect information necessary to provide our taxi services:
          • Name and contact details (phone, email)
          • Pickup and destination addresses
          • Booking information (date, time, number of passengers)
          • Service preferences (minivan, accessible, etc.)`
        },
        {
          title: 'Use of Information',
          content: `Your information is used to:
          • Process your taxi reservations
          • Contact you regarding your ride
          • Improve our services
          • Comply with legal obligations`
        },
        {
          title: 'Information Sharing',
          content: `We do not sell or share your personal information with third parties, except:
          • With our drivers to complete your ride
          • If required by law
          • To protect our legal rights`
        },
        {
          title: 'Security',
          content: `We take reasonable measures to protect your information from unauthorized access, loss, or destruction.`
        },
        {
          title: 'Your Rights',
          content: `You have the right to:
          • Access your personal information
          • Request correction of inaccurate information
          • Request deletion of your data
          
          To exercise these rights, contact us at ${siteConfig.phone}.`
        },
      ],
    },
  };

  const c = content[language];

  return (
    <div className="min-h-screen pt-20 pb-20" data-testid="privacy-page">
      <SEO {...pageSEO.privacy} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Button asChild variant="ghost" className="mb-8">
          <Link to={getRoute('/')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Retour' : 'Back'}
          </Link>
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">{c.title}</h1>
            <p className="text-muted-foreground text-sm">{c.lastUpdated}</p>
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            <p className="text-muted-foreground mb-8">{c.intro}</p>
            
            <div className="space-y-8">
              {c.sections.map((section, index) => (
                <div key={index}>
                  <h2 className="font-heading font-bold text-xl text-foreground mb-3">
                    {section.title}
                  </h2>
                  <p className="text-muted-foreground whitespace-pre-line">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPage;
